import React, { useMemo, useState } from "react";
import { useQueryClient, useQuery, useQueries } from "@tanstack/react-query";
import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  Grid,
  Tabs,
  Tab,
  Stack,
  Skeleton,
  Pagination,
} from "@mui/material";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import { PrivateContent, SocialAccount } from "../services/api";
import { apiService } from "../services/api";

import TikTokIcon from "./icons/TikTokIcon";
import SpotifyIcon from "./icons/SpotifyIcon";
import { PrivateContentCard } from "./content-tabs/PrivateContentCard";
import { ContentDetailsDialog } from "./content-dialog/ContentDetailsDialog";
import { Folder as FolderIcon } from "@mui/icons-material";

const VERTICAL_TAB_KEYS = [
  "youtubeShorts",
  "instagramReels",
  "instagramPosts",
  "tiktokContent",
  "facebookContent",
] as const;

const EPISODIO_GRID_SIZE = { xs: 12, sm: 6, md: 3, lg: 3, xl: 3 };
const VERTICAL_GRID_SIZE = { xs: 12, sm: 6, md: 1.5, lg: 1.5, xl: 1.5 };
const DEFAULT_GRID_SIZE = { xs: 12, sm: 6, md: 3, lg: 3, xl: 2 };

type ContentSkeletonVariant = "vertical" | "horizontal";

type ContentGridSize = {
  xs: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
};

function isVerticalTabKey(tabKey: string): boolean {
  return (VERTICAL_TAB_KEYS as readonly string[]).includes(tabKey);
}

function getContentSkeletonLayout(tabKey?: string): {
  variant: ContentSkeletonVariant;
  gridSize: ContentGridSize;
  count: number;
} {
  if (tabKey && isVerticalTabKey(tabKey)) {
    return { variant: "vertical", gridSize: VERTICAL_GRID_SIZE, count: 16 };
  }
  if (tabKey === "youtubeVideos") {
    return { variant: "horizontal", gridSize: EPISODIO_GRID_SIZE, count: 8 };
  }
  return { variant: "horizontal", gridSize: DEFAULT_GRID_SIZE, count: 12 };
}

const HorizontalContentCardSkeleton: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      position: "relative",
      height: "100%",
    }}
  >
    <Box sx={{ borderRadius: 1, overflow: "hidden", flexShrink: 0 }}>
      <Skeleton variant="rectangular" width="100%" height={160} />
    </Box>
    <Box
      sx={{
        pt: 1,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Skeleton variant="text" width="90%" height={20} />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Skeleton variant="circular" width={14} height={14} />
          <Skeleton variant="text" width={40} height={14} />
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Skeleton variant="circular" width={14} height={14} />
          <Skeleton variant="text" width={32} height={14} />
        </Stack>
      </Box>
    </Box>
  </Box>
);

const VerticalContentCardSkeleton: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      position: "relative",
      height: "100%",
    }}
  >
    <Box
      sx={{
        width: "100%",
        aspectRatio: "9/16",
        borderRadius: 1,
        overflow: "hidden",
        flexShrink: 0,
        position: "relative",
      }}
    >
      <Skeleton
        variant="rectangular"
        width="100%"
        height="100%"
        sx={{ display: "block", transform: "none" }}
      />
    </Box>
    <Box
      sx={{
        pt: 1,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Skeleton variant="text" width="90%" height={16} />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Skeleton variant="circular" width={14} height={14} />
          <Skeleton variant="text" width={36} height={12} />
        </Stack>
        <Skeleton variant="text" width="55%" height={12} />
      </Box>
    </Box>
  </Box>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface EmptyStateProps {
  icon: React.ReactNode;
  message: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({ icon, message }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    spacing={3}
    sx={{
      py: 8,
      px: 3,
      height: "100%",
      minHeight: 300,
      textAlign: "center",
      color: "text.secondary",
      borderRadius: 2,
      border: "1px dashed",
      borderColor: "grey.300",
    }}
  >
    <Stack spacing={2} alignItems="center">
      <FolderIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />

      <Typography variant="h6" color="text.primary" sx={{ fontWeight: 500 }}>
        {message}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
        El contenido aparecerá aquí una vez que esté disponible para este
        podcast.
      </Typography>
    </Stack>
  </Stack>
);

const ContentTabsSkeleton: React.FC<{ tabKey?: string }> = ({ tabKey }) => (
  <Box>
    {/* Tabs skeleton */}
    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          pt: 1,
        }}
      >
        <Stack direction="row" spacing={2}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width={120}
              height={32}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Stack>
      </Box>
    </Box>

    <ContentGridSkeleton tabKey={tabKey} />
  </Box>
);

/** Skeleton for the content grid only (used when refetching search/filters; tabs stay visible) */
export const ContentGridSkeleton: React.FC<{ tabKey?: string }> = ({ tabKey }) => {
  const { variant, gridSize, count } = getContentSkeletonLayout(tabKey);
  const CardSkeleton =
    variant === "vertical" ? VerticalContentCardSkeleton : HorizontalContentCardSkeleton;

  return (
    <Grid container spacing={3}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid key={index} size={gridSize}>
          <CardSkeleton />
        </Grid>
      ))}
    </Grid>
  );
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`content-tabpanel-${index}`}
      aria-labelledby={`content-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1, pb: 2 }}>{children}</Box>}
    </div>
  );
}

interface ContentTabsProps {
  podcastId?: string;
  programId?: string | null;
  brandId?: string | null;
  monthFilter?: string | null; // Filter by month in format YYYY-MM
  searchTerm?: string;
  isLoading?: boolean;
  colsMedium?: number;
  colsLarge?: number;
  socialAccounts?: SocialAccount[];
  allBrands?: Array<{
    _id: string;
    name: string;
    logoUrl?: string;
    mentionCount: number;
    presenceCount: number;
  }>;
  selectedBrandId?: string | null;
  /** When true, content detail dialog shows livestream timeline (company podcast setting). Default false. */
  showLivestreamData?: boolean;
  /** Scope content queries to this specific company (tenant isolation). */
  companyId?: string;
}

export const ContentTabs: React.FC<ContentTabsProps> = ({
  podcastId,
  programId,
  brandId,
  monthFilter,
  searchTerm,
  isLoading,
  colsMedium = 4,
  colsLarge = 3,
  socialAccounts = [],
  allBrands,
  selectedBrandId,
  showLivestreamData = false,
  companyId,
}) => {
  const queryClient = useQueryClient();

  const [tabValue, setTabValue] = React.useState(0);
  const [selectedContentId, setSelectedContentId] = useState<string | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setCurrentPage(1); // Reset to first page when switching tabs
  };

  const handleContentClick = (content: PrivateContent) => {
    setSelectedContentId(String(content._id));
  };

  // Reset page to 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [programId, brandId, monthFilter, searchTerm]);

  const handleCloseDialog = () => {
    setSelectedContentId(null);
  };

  const handleBrandPresenceAdded = () => {
    // Invalidate relevant queries to refresh brand data
    queryClient.invalidateQueries({ queryKey: ["podcast-brands"] });
    queryClient.invalidateQueries({ queryKey: ["tab-content"] });
    queryClient.invalidateQueries({ queryKey: ["private-content-by-id"] });
  };

  const {
    data: detailContent,
    isLoading: detailLoading,
    isError: detailError,
  } = useQuery({
    queryKey: ["private-content-by-id", selectedContentId],
    queryFn: () => apiService.getPrivateContentById(selectedContentId!),
    enabled: !!selectedContentId,
    staleTime: 60 * 1000,
  });

  React.useEffect(() => {
    if (detailError && selectedContentId) {
      setSelectedContentId(null);
    }
  }, [detailError, selectedContentId]);

  // Define all possible tabs
  const allTabs = useMemo(
    () => [
      {
        key: "youtubeVideos",
        label: "Episodios",
        icon: <YouTubeIcon sx={{ fontSize: 20, color: "#FF0000" }} />,
      },
      {
        key: "spotifyContent",
        label: "Episodios",
        icon: <SpotifyIcon sx={{ fontSize: 20, color: "#000" }} />,
      },
      {
        key: "youtubeShorts",
        label: "Shorts",
        icon: <YouTubeIcon sx={{ fontSize: 20, color: "#FF0000" }} />,
      },
      {
        key: "instagramReels",
        label: "Reels",
        icon: <InstagramIcon sx={{ fontSize: 20, color: "#E1306C" }} />,
      },
      {
        key: "instagramPosts",
        label: "Posts",
        icon: <InstagramIcon sx={{ fontSize: 20, color: "#E1306C" }} />,
      },
      {
        key: "tiktokContent",
        label: "Videos",
        icon: <TikTokIcon sx={{ fontSize: 20, color: "#000000" }} />,
      },
      {
        key: "facebookContent",
        label: "Posts",
        icon: <FacebookIcon sx={{ fontSize: 20, color: "#1877F2" }} />,
      },
    ],
    [],
  );

  // Fetch tab counts independently for each tab
  const { data: tabCounts, isLoading: tabCountsLoading } = useQuery({
    queryKey: [
      "tab-counts",
      podcastId,
      programId,
      brandId,
      monthFilter,
      searchTerm,
      companyId,
    ],
    queryFn: async () => {
      if (!podcastId) return {};
      const counts: Record<string, number> = {};

      // Fetch count for each tab in parallel (page 1, limit 1 to get total count)
      await Promise.all(
        allTabs.map(async (tab) => {
          try {
            const result = await apiService.getPrivateCollectionsContent(
              podcastId,
              {
                programId: programId || undefined,
                brandId: brandId || undefined,
                monthFilter: monthFilter || undefined,
                searchTerm: searchTerm || undefined,
                tab: tab.key,
                page: 1,
                limit: 1,
                companyId: companyId || undefined,
              },
            );
            counts[tab.key] = result?.pagination?.totalItems || 0;
          } catch (error) {
            counts[tab.key] = 0;
          }
        }),
      );
      return counts;
    },
    enabled: !!podcastId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Determine if we're still waiting for tab counts
  const isWaitingForTabCounts = !tabCounts && !!podcastId;

  // Show tabs that have content; always show YouTube Shorts so users can open an empty grid vs a missing tab
  const availableTabs = useMemo(() => {
    if (!tabCounts) return []; // Return empty if we don't have counts yet
    return allTabs.filter(
      (tab) =>
        (tabCounts[tab.key] || 0) > 0 || tab.key === "youtubeShorts",
    );
  }, [allTabs, tabCounts]);

  // Get current tab
  const currentTab = availableTabs[tabValue];

  // Fetch content for current tab
  const { data: tabContent, isLoading: tabContentLoading } = useQuery({
    queryKey: [
      "tab-content",
      podcastId,
      currentTab?.key,
      programId,
      brandId,
      monthFilter,
      searchTerm,
      currentPage,
      companyId,
    ],
    queryFn: async () => {
      if (!podcastId || !currentTab?.key) return null;
      return await apiService.getPrivateCollectionsContent(podcastId, {
        programId: programId || undefined,
        brandId: brandId || undefined,
        monthFilter: monthFilter || undefined,
        searchTerm: searchTerm || undefined,
        tab: currentTab.key,
        page: currentPage,
        limit:
          currentTab.key === "youtubeVideos"
            ? 8
            : isVerticalTabKey(currentTab.key)
              ? 16
              : 12,
        companyId: companyId || undefined,
      });
    },
    enabled: !!podcastId && !!currentTab?.key,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes for instant tab switching
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch brand presence data for selected content (same id as detail fetch)
  const { data: brandPresence } = useQuery({
    queryKey: ["brand-presence", selectedContentId],
    queryFn: async () => {
      if (!selectedContentId) return {};
      const [brandMentions, brandPresence] = await Promise.all([
        apiService.getPrivateBrandMentions([selectedContentId]),
        apiService.getPrivateBrandPresence([selectedContentId]),
      ]);
      return { brandMentions, brandPresence };
    },
    enabled: !!selectedContentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const showDetailBackdrop =
    !!selectedContentId && detailLoading && !detailContent;

  const currentContent = useMemo(
    () =>
      tabContent?.allContent
        ? (Object.values(tabContent.allContent).flat() as PrivateContent[])
        : [],
    [tabContent],
  );

  const pageContentIds = useMemo(
    () => currentContent.map((item) => String(item._id)),
    [currentContent],
  );
  const selectedIndex = selectedContentId
    ? pageContentIds.indexOf(selectedContentId)
    : -1;
  const hasPrevious = selectedIndex > 0;
  const hasNext =
    selectedIndex >= 0 && selectedIndex < pageContentIds.length - 1;

  const handleNavigatePrevious = React.useCallback(() => {
    if (selectedIndex <= 0) return;
    setSelectedContentId(pageContentIds[selectedIndex - 1] ?? null);
  }, [pageContentIds, selectedIndex]);

  const handleNavigateNext = React.useCallback(() => {
    if (selectedIndex < 0 || selectedIndex >= pageContentIds.length - 1) return;
    setSelectedContentId(pageContentIds[selectedIndex + 1] ?? null);
  }, [pageContentIds, selectedIndex]);

  const tabListDetailReady = !tabContentLoading && !!tabContent;

  const tabContentDetailQueries = useQueries({
    queries: currentContent.map((item) => ({
      queryKey: ["private-content-by-id", String(item._id)],
      queryFn: () => apiService.getPrivateContentById(String(item._id)),
      staleTime: 60 * 1000,
      enabled: tabListDetailReady && !!item._id,
    })),
  });

  // Auto-switch to first available tab if current tab becomes unavailable
  React.useEffect(() => {
    if (availableTabs.length > 0 && tabValue >= availableTabs.length) {
      setTabValue(0); // Switch to first available tab
    }
  }, [availableTabs, tabValue]);

  // Full skeleton only when we don't have tab structure yet (initial load or tab counts loading)
  const showFullSkeleton =
    isLoading || tabCountsLoading || isWaitingForTabCounts;
  if (showFullSkeleton) {
    return <ContentTabsSkeleton tabKey={currentTab?.key} />;
  }

  if (!podcastId) {
    return (
      <EmptyState
        icon={<VideoLibraryIcon />}
        message="No hay podcast seleccionado"
      />
    );
  }

  // If no tabs have content, show empty state
  if (availableTabs.length === 0) {
    return (
      <EmptyState
        icon={<VideoLibraryIcon />}
        message="No hay contenido disponible"
      />
    );
  }

  // When only content is refetching (e.g. search/filter change), show tabs + content skeleton
  const showContentSkeletonOnly = tabContentLoading;

  return (
    <>
      <Box>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  "& .MuiTabs-flexContainer": {
                    flexWrap: "wrap",
                    gap: 1,
                  },
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}
              >
                {availableTabs.map((tab, index) => (
                  <Tab
                    key={index}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        {tab.icon}
                        <span>{tab.label}</span>
                        {tabCounts?.[tab.key] !== undefined && (
                          <Typography variant="caption" color="text.secondary">
                            ({tabCounts[tab.key]})
                          </Typography>
                        )}
                      </Stack>
                    }
                    sx={{
                      borderBottom: tabValue === index ? 2 : 0,
                      borderColor:
                        tabValue === index ? "primary.main" : "transparent",
                      minHeight: 48,
                      borderRadius: "0 !important",
                      "& .MuiTab-root": {
                        borderRadius: "0 !important",
                      },
                      "&.MuiButtonBase-root": {
                        borderRadius: "0 !important",
                      },
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          </Box>
        </Box>

        <TabPanel value={tabValue} index={tabValue}>
          {showContentSkeletonOnly ? (
            <ContentGridSkeleton tabKey={currentTab?.key} />
          ) : currentContent.length === 0 ? (
            <EmptyState
              icon={currentTab?.icon}
              message={
                currentTab?.label === "Spotify"
                  ? "No hay episodios de Spotify"
                  : currentTab?.label === "YouTube"
                    ? "No hay videos de YouTube"
                    : currentTab?.label === "Instagram"
                      ? "No hay contenido de Instagram"
                      : currentTab?.label === "TikTok"
                        ? "No hay videos de TikTok"
                        : currentTab?.label === "Facebook"
                          ? "No hay contenido de Facebook"
                          : `No hay contenido de ${currentTab?.label}`
              }
            />
          ) : (
            <Box>
              <Box sx={{ minHeight: 440 }}>
                <Grid container spacing={3}>
                  {currentContent.map((item: PrivateContent, index: number) => {
                    const isVerticalTab =
                      currentTab?.key && isVerticalTabKey(currentTab.key);
                    const isYoutubeVideos = currentTab?.key === "youtubeVideos";
                    const gridSize = isYoutubeVideos
                      ? EPISODIO_GRID_SIZE
                      : isVerticalTab
                        ? VERTICAL_GRID_SIZE
                        : DEFAULT_GRID_SIZE;
                    const enrichedItem =
                      tabContentDetailQueries[index]?.data ?? item;
                    console.log("enrichedItem", enrichedItem);
                    return (
                      <Grid key={item._id} size={gridSize}>
                        <PrivateContentCard
                          item={enrichedItem}
                          onClick={handleContentClick}
                          imageAspectRatio={
                            isVerticalTab ? "vertical" : "standard"
                          }
                          showLivestreamData={showLivestreamData}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              {/* Pagination */}
              {tabContent?.pagination &&
                tabContent.pagination.totalPages > 1 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                  >
                    <Pagination
                      count={tabContent.pagination.totalPages}
                      page={currentPage}
                      onChange={(_, page) => setCurrentPage(page)}
                      color="primary"
                      size="large"
                    />
                  </Box>
                )}
            </Box>
          )}
        </TabPanel>
      </Box>

      <Backdrop
        open={showDetailBackdrop}
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Content Details Dialog — full document via getPrivateContentById (explorer enrichment) */}
      <ContentDetailsDialog
        open={Boolean(selectedContentId && detailContent)}
        onClose={handleCloseDialog}
        content={detailContent ?? null}
        allBrands={allBrands}
        brandPresence={brandPresence?.brandPresence}
        selectedBrandId={selectedBrandId}
        onBrandPresenceAdded={handleBrandPresenceAdded}
        showLivestreamData={showLivestreamData}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
      />
    </>
  );
};
